import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapboxComponent } from '../../shared/mapbox/mapbox.component';
import { HeroReportCardComponent } from './hero-report-card/hero-report-card.component';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { UserMenuComponent } from '../../shared/user-menu/user-menu.component';
import { MapLayersModalComponent } from '../../shared/map-layers-modal/map-layers-modal.component';
import { PermissionService } from '../../permissions.service';
import { MapLayerService } from '../../model/map-layer/map-layer.service';
import { MapLayer } from '../../model/map-layer/map-layer.model';
import { HeroReport } from '../../model/hero-report/hero-report.model';
import { ActivatedRoute, Router } from '@angular/router';
import { MapLayerDialogComponent } from '../../shared/map-layer-dialog/map-layer-dialog.component';
import { HeroReportModalComponent } from '../hero-report/hero-report-modal/hero-report-modal.component';
import { ButtonGroupComponent } from '../../shared/button-group/button-group.component';
import { take } from 'rxjs';
import { EventFiltersComponent } from './event-filters/event-filters.component';
import { ExportReportsComponent } from './export-reports/export-reports.component';
import { EventFilterValues } from '../../utilities/reportExport';
import { HeroReportService } from '../../model/hero-report/hero-report.service';
import { Organization } from '../../model/organization/organization.model';
import { Region } from '../../model/region/region.model';
import { Account } from '../../model/account/account.model';
import { LocalizationService } from '../../shared/localization.service';
import { AccountService } from '../../model/account/account.service';
import { MapPreferencesService } from '../../shared/map-preferences.service';

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [
    CommonModule,
    MapboxComponent,
    HeroReportCardComponent,
    DropdownModule,
    ButtonModule,
    FormsModule,
    OverlayPanelModule,
    UserMenuComponent,
    MapLayersModalComponent,
    MapLayerDialogComponent,
    HeroReportModalComponent,
    ButtonGroupComponent,
    ReactiveFormsModule,
    EventFiltersComponent,
    ExportReportsComponent,
  ],
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
})
export class EventsComponent implements OnInit, AfterViewInit {
  @ViewChild(MapboxComponent) mapboxComponent!: MapboxComponent;
  @ViewChild(HeroReportModalComponent)
  heroReportModal!: HeroReportModalComponent;

  events: HeroReport[] = [];
  filteredEvents: HeroReport[] = [];
  filteredEventsInView: HeroReport[] = [];
  mapLayers: MapLayer[] = [];

  selectedHero: HeroReport | null = null;
  showPanel = false;
  showLayersModal = false;
  isLoading = false;
  isMapLoading = false;
  latitude: number | null = null;
  longitude: number | null = null;
  selectedMapType = 'streets';
  showEvacuationRadius = false;
  showSwimLanes = false;
  showPendingEvents = true;
  showCompletedEvents = true;
  showEventsOutsideView = true;
  canViewEod = false;
  canEditMapLayer = false;
  mapLayerSaveError: string | null = null;
  organizations: Organization[] = [];
  regions: Region[] = [];
  isSidebarCollapsed = false;

  /** The filters as last set above the events; the export report uses them. */
  currentFilters: EventFilterValues | null = null;

  selectedLayer: any = null;

  viewOptions = [
    {
      label: $localize`:@@view.list:List`,
      value: 'list'
    },
    {
      label: $localize`:@@view.map:Map`,
      value: 'map'
    }
  ];

  viewForm!: FormGroup;

  account: Account;

  constructor(
    private mapLayerService: MapLayerService,
    private permissionService: PermissionService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private heroReportService: HeroReportService,
    private localizationService: LocalizationService,
    private accountService: AccountService,
    private mapPreferencesService: MapPreferencesService
  ) { }

  ngOnInit() {
    this.loadMapPreferences();

    this.route.data.subscribe((data) => {
      this.events = data['reports'];
      this.mapLayers = data['mapLayers'];
      this.organizations = data['organizations'];
      this.regions = data['regions'];
      this.account = data['account']

      this.updateFilteredEvents();
      this.fetchUserLocation();
      this.canViewEod = this.permissionService.can('view:eod');
      this.canEditMapLayer = this.permissionService.can('edit:map-layer');
    });

    this.setupViewForm();
    this.redirectToSelectedLanguage();
  }

  private loadMapPreferences(): void {
    const prefs = this.mapPreferencesService.load();
    this.selectedMapType = prefs.selectedMapType;
    this.showEvacuationRadius = prefs.showEvacuationRadius;
    this.showPendingEvents = prefs.showPendingEvents;
    this.showCompletedEvents = prefs.showCompletedEvents;
    this.showEventsOutsideView = prefs.showEventsOutsideView;
  }

  private async redirectToSelectedLanguage() {
    let locale = this.localizationService.loadLocalization();

    if (!locale) {
      try {
        const account = await this.accountService.fetch();
        locale = this.localizationService.getLocaleFromEnum(account.language);
        this.localizationService.saveLocalization(locale);
        this.localizationService.applicationRedirect('events');

      } catch (error) {
        console.error('Error fetching account language:', error);
      }
    }
  }

  private setupViewForm() {
    this.viewForm = this.formBuilder.group({
      view: ['map'],
    });

    this.viewForm
      .get('view')
      ?.valueChanges.pipe(take(1))
      .subscribe((value) => {
        if (value === 'map') {
          setTimeout(() => {
            this.mapboxComponent.map.resize();
          }, 0);
        }
      });
  }

  ngAfterViewInit() {
    this.mapboxComponent.map.on('moveend', () => {
      if (!this.showEventsOutsideView) {
        this.updateFilteredEventsInView()
      }
    })
  }

  private async fetchMapLayers() {
    try {
      this.mapLayers = await this.mapLayerService.fetch();
    } catch (error) {
      console.error('Error fetching map layers:', error);
    }
  }

  private async fetchEvents(filters?: any) {
    try {
      this.events = await this.heroReportService.fetch(filters);
      this.updateFilteredEvents();
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }


  onFiltersChanged(filters: any) {
    this.currentFilters = filters;
    this.fetchEvents(filters);
  }

  private async fetchUserLocation() {
    const recenterParam = this.route.snapshot.queryParamMap.get('recenter');
    if (recenterParam === 'true') {
      const storedCenter = localStorage.getItem('mapCenter');
      if (storedCenter) {
        const [lng, lat] = storedCenter.split(',').map(Number);
        this.longitude = lng;
        this.latitude = lat;
        this.isMapLoading = false;
        return;
      }
    }
    if (navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          }
        );
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.isMapLoading = false;
      } catch (error) {
        this.latitude = 40.7128;
        this.longitude = -74.006;
        this.isMapLoading = false;
      }
    } else {
      this.latitude = 40.7128;
      this.longitude = -74.006;
      this.isMapLoading = false;
    }
  }

  async addMapLayer(newLayer: MapLayer) {
    try {
      await this.mapLayerService.create(newLayer);
      await this.fetchMapLayers();
    } catch (errorResponse) {
      const {
        error: {
          data: { errorCode },
        },
      } = errorResponse;
      if (errorCode === 'MAP_LAYER_ALREADY_EXISTS_EXCEPTION') {
        this.mapLayerSaveError = $localize`:@@mapLayer.alreadyExists:Map layer already exists`;
      } else {
        this.mapLayerSaveError = $localize`:@@mapLayer.failedToAdd:Failed to add map layer`;
      }
      console.error('Error adding map layer:', errorResponse);
    }
  }

  async updateMapLayer(updatedLayer: MapLayer) {
    try {
      if (!updatedLayer.createdDate) {
        await this.mapLayerService.create(updatedLayer);
      } else {
        await this.mapLayerService.update(updatedLayer);
      }
      await this.fetchMapLayers();
      this.selectedLayer = null;
    } catch (errorResponse) {
      const {
        error: {
          data: { errorCode },
        },
      } = errorResponse;
      if (errorCode === 'MAP_LAYER_ALREADY_EXISTS_EXCEPTION') {
        this.mapLayerSaveError = $localize`:@@mapLayer.alreadyExists:Map layer already exists`;
      } else {
        if (updatedLayer.createdDate)
          this.mapLayerSaveError = $localize`:@@mapLayer.failedToUpdate:Failed to update map layer`;
        else {
          this.mapLayerSaveError = $localize`:@@mapLayer.failedToAdd:Failed to add map layer`;
        }
      }
      console.error('Error updating map layer:', errorResponse);
    }
  }

  async deleteMapLayer(layerId: string) {
    try {
      await this.mapLayerService.delete(layerId);
      await this.fetchMapLayers();
      this.selectedLayer = null;
    } catch (error) {
      console.error('Error deleting map layer:', error);
    }
  }

  onLayersClick() {
    this.showLayersModal = true;
  }

  onApplyLayers(event: {
    selectedMapType: string;
    showEvacuationRadius: boolean;
    showSwimLanes: boolean;
    showPendingEvents: boolean;
    showCompletedEvents: boolean;
    showEventsOutsideView: boolean;
  }) {
    const mapTypeChanged = this.selectedMapType !== event.selectedMapType;
    const evacRadiusChanged =
      this.showEvacuationRadius !== event.showEvacuationRadius;

    this.selectedMapType = event.selectedMapType;
    this.showEvacuationRadius = event.showEvacuationRadius;
    this.showSwimLanes = event.showSwimLanes;
    this.showPendingEvents = event.showPendingEvents;
    this.showCompletedEvents = event.showCompletedEvents;
    this.showEventsOutsideView = event.showEventsOutsideView;

    this.mapPreferencesService.save({
      selectedMapType: this.selectedMapType,
      showEvacuationRadius: this.showEvacuationRadius,
      showPendingEvents: this.showPendingEvents,
      showCompletedEvents: this.showCompletedEvents,
      showEventsOutsideView: this.showEventsOutsideView,
    });

    this.updateFilteredEvents();
    if (!this.showEventsOutsideView) {
      this.updateFilteredEventsInView();
    }

    if (
      this.mapboxComponent &&
      this.mapboxComponent.map &&
      (mapTypeChanged || evacRadiusChanged)
    ) {
      this.mapboxComponent.changeMap(this.selectedMapType, this.showEvacuationRadius);
    }
  }

  onHeroCardClicked(report: HeroReport) {
    this.mapboxComponent.flyTo(report.latitude, report.longitude);
  }

  onZoomOutClick() {
    this.mapboxComponent.zoomOut();
  }

  onZoomInClick() {
    this.mapboxComponent.zoomIn();
  }

  onMapLayerAdd() {
    this.mapboxComponent.startPolygonMode();
  }

  onMapLayerDelete() {
    if (this.selectedLayer && this.selectedLayer.id) {
      const layerId = this.selectedLayer.id;
      this.mapboxComponent.deleteSelectedLayer(layerId);
      this.deleteMapLayer(layerId);
    }
  }

  onLayerSelected(feature: any) {
    if (!feature) {
      this.selectedLayer = null;
      return;
    } else {
      // If the layer is already in the mapLayers array, use it, otherwise use the feature.
      // This is to handle the case where the layer is added from the map.
      // For update, I want to use what we already have in the mapLayers array because
      // there are a bunch of other properties that we don't want to lose.
      const originalLayer =
        this.mapLayers.find((layer) => layer.id === feature.id) || {};

      if (originalLayer.id) {
        // Create a deep clone of the layer to work with in the dialog
        // This allows us to make visual changes without affecting the original until save is clicked
        this.selectedLayer = JSON.parse(JSON.stringify(originalLayer));
      } else {
        // For new layers, add to the mapLayers array
        this.mapLayers.push(feature);
        this.selectedLayer = feature;
      }
    }
  }

  onMarkerClicked(report: HeroReport) {
    this.selectedHero = report;
    this.showPanel = true;
  }

  closePanel() {
    this.selectedHero = null;
    this.showPanel = false;
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    if (this.mapboxComponent && this.mapboxComponent.map) {
      setTimeout(() => {
        this.mapboxComponent.map.resize();
      }, 300);
    }
  }

  onAddEvent() {
    this.heroReportModal.show();
  }

  private async updateFilteredEvents() {
    this.filteredEvents = this.events.filter(event => {
      const status = event.status ? event.status.toLowerCase() : '';

      if (status === 'pending' && !this.showPendingEvents) {
        return false;
      }
      if (status === 'completed' && !this.showCompletedEvents) {
        return false;
      }

      return true;
    });
  }

  private async updateFilteredEventsInView() {
    this.filteredEventsInView = this.filteredEvents.filter(event => {
      if (this.mapboxComponent) {
        if (!this.mapboxComponent.map.getBounds().contains([event.longitude, event.latitude])) {
          return false;
        }
      }

      return true;
    })
  }

  onHeroReportSave(event: { coordinates: string; lat: number; lng: number; reportType?: 'UXO' | 'SRE' }) {
    this.router.navigate([event.reportType === 'SRE' ? '/sre-report' : '/hero-report'], {
      queryParams: { lat: event.lat, lng: event.lng },
    });
  }

  onCreateHeroReportFromCoordinates(coords: {
    latitude: number;
    longitude: number;
  }) {
    this.router.navigate(['/hero-report'], {
      queryParams: { lat: coords.latitude, lng: coords.longitude },
    });
  }

  onCreateSreReportFromCoordinates(coords: {
    latitude: number;
    longitude: number;
  }) {
    this.router.navigate(['/sre-report'], {
      queryParams: { lat: coords.latitude, lng: coords.longitude },
    });
  }

  onCloseLayerDialog() {
    // Handle the case where we have a temporary layer that hasn't been saved to the server
    if (this.selectedLayer) {
      // Check if this is a new, unsaved layer (no id in the database)
      const isNewLayer = !this.selectedLayer.createdDate;

      if (isNewLayer && this.mapboxComponent) {
        const featureId = this.selectedLayer.id;
        if (featureId) {
          if (this.mapboxComponent.draw) {
            try {
              this.mapboxComponent.draw.deleteAll();

              this.mapboxComponent.draw.changeMode('simple_select');
            } catch (e) {
              console.log('Unable to delete from draw tool', e);
            }
          }

          try {
            const map = this.mapboxComponent.map;
            const polygonLayerId = 'polygon-layer-' + featureId;
            const polygonSourceId = 'polygon-source-' + featureId;
            const gridLayerId = 'grid-layer-' + featureId;
            const gridSourceId = 'grid-source-' + featureId;

            if (map.getLayer(polygonLayerId)) map.removeLayer(polygonLayerId);
            if (map.getLayer(gridLayerId)) map.removeLayer(gridLayerId);

            if (map.getSource(polygonSourceId)) map.removeSource(polygonSourceId);
            if (map.getSource(gridSourceId)) map.removeSource(gridSourceId);
          } catch (e) {
            console.error('Error removing map elements', e);
          }
        }
      }
    }

    // Clear the selected layer and fetch saved layers from the server
    this.selectedLayer = null;
    this.fetchMapLayers();
  }

  onLayerPreviewChange(updatedLayer: any) {
    if (!updatedLayer || !this.selectedLayer) return;

    const parse = (v: any) => {
      if (!v) return {};
      if (typeof v === 'string') {
        try { return JSON.parse(v); }
        catch { return {}; }
      }
      return v;
    };

    const base = parse(this.selectedLayer.jsonContent);
    const incomingContent = parse(updatedLayer.jsonContent);
    const incomingMeta = parse(updatedLayer.jsonMetadata);

    const merged = { ...base, ...incomingContent, ...incomingMeta };

    this.selectedLayer.jsonContent = merged;
    this.selectedLayer.jsonMetadata = incomingMeta;

    Object.assign(this.selectedLayer, {
      name: updatedLayer.name,
      description: updatedLayer.description,
      organizationId: updatedLayer.organizationId,
    });

    if (this.mapboxComponent) {
      this.mapboxComponent.updateGridLayerForPolygon({
        id: merged.id,
        geometry: merged.geometry,
        properties: {
          gridColor: merged.color,
          gutterSpacing: merged.gutterSpacing,
          lineWidth: merged.lineWidth,
          showSwimLanes: merged.showSwimLanes,
          rotationDegrees: merged.rotationDegrees,
        }
      });
    }
  }
}
